import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import './FindProductByMid.css';
import '../../styles/hot-table-status-pills.css';
import { apiErrToMessage } from '../../utils/apiError';
import { hotStatusPillRenderer, plainStatusForExport } from '../../utils/hotTableStatusRenderer';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import {
    Card,
    Row,
    Col,
    Button,
    Modal,
    List,
    Space,
    Typography,
    Divider,
    Empty,
    Spin,
    message,
} from 'antd';
import {
    HistoryOutlined,
    DownloadOutlined,
    SearchOutlined,
    ReloadOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';

const { Text } = Typography;

const COL_WIDTHS_STORAGE_KEY = 'findProductByMid_colWidths';
const DEFAULT_COL_WIDTHS = [100, 100, 200, 100, 200, 80, 150, 100, 150, 100];

const loadSavedColWidths = () => {
    try {
        const saved = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
        if (!saved) return null;
        const arr = JSON.parse(saved);
        return Array.isArray(arr) && arr.length > 0 ? arr : null;
    } catch {
        return null;
    }
};

const saveColWidths = widths => {
    try {
        localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (e) {
        console.warn('colWidths 저장 실패:', e);
    }
};

registerAllModules();

function isValidMid(value) {
    return /^\d+$/.test(value.trim());
}

const FindProductByMid = ({ title: _title = '상품정보 조회' }) => {
    const hotRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [showGuide, _setShowGuide] = useState(() => {
        try {
            const saved = localStorage.getItem('findProductByMid_showGuide');
            return saved !== null ? JSON.parse(saved) : true;
        } catch {
            return true;
        }
    });

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const PAGE_SIZE = 10;
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [maxRows, setMaxRows] = useState(20);
    const [tableData, setTableData] = useState([]);
    const [colWidths, setColWidths] = useState(() => {
        const saved = loadSavedColWidths();
        if (saved && saved.length === DEFAULT_COL_WIDTHS.length) return [...saved];
        return [...DEFAULT_COL_WIDTHS];
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get('/settings/rank-limit', {
                    params: { gubun: 'findProductByMid' },
                });

                const rows = data?.success ? data.max_rows : 20;
                setMaxRows(rows);
                setTableData(Array.from({ length: rows }, () => Array(10).fill('')));
            } catch (err) {
                console.error('⚠️ 행 제한 조회 실패:', err);
                setMaxRows(20);
                setTableData(Array.from({ length: 20 }, () => Array(10).fill('')));
            }
        })();
    }, []);

    const handleMidSearch = async () => {
        if (loading) return;
        setLoading(true);

        try {
            const currentData = [...tableData];
            const hot = hotRef.current.hotInstance;

            const validProducts = currentData
                .map((row, idx) => {
                    const keyword = row[0]?.trim();
                    const mid = row[1]?.trim();
                    if (keyword && mid && isValidMid(mid)) {
                        currentData[idx][9] = '<div class="loading-spinner"></div>';
                        hot.setCellMeta(idx, 9, 'className', 'processing-row');
                        return { index: idx, keyword, mid };
                    }
                    return null;
                })
                .filter(Boolean);

            if (validProducts.length === 0) {
                message.warning(
                    '키워드와 상품번호 or MID를 모두 입력한 행이 1개 이상 있어야 합니다.'
                );
                setLoading(false);
                return;
            }

            const response = await AuthService.authAxios.post('/product/findProductByMid', {
                products: validProducts,
            });

            const result = response.data;

            if (result.success && Array.isArray(result.results)) {
                const results = result.results;

                results.forEach(item => {
                    const i = item.index;
                    currentData[i][2] = item.link || '';
                    currentData[i][3] = '';
                    currentData[i][4] = item.title || '';
                    currentData[i][5] = item.mallName || '';
                    currentData[i][6] = item.image || '';
                    currentData[i][7] = item.lprice ? item.lprice.toLocaleString() : '';
                    currentData[i][8] = Array.isArray(item.detailInfo)
                        ? item.detailInfo.join(', ')
                        : item.detailInfo || '';

                    if (item.productMid || item.title) {
                        currentData[i][9] = '성공';
                        hot.setCellMeta(i, 9, 'className', 'completed-row');
                    } else if (item.error) {
                        currentData[i][9] = '실패';
                        hot.setCellMeta(i, 9, 'className', 'error-row');
                    } else {
                        currentData[i][9] = '값 없음';
                        hot.setCellMeta(i, 9, 'className', 'completed-row');
                    }
                });

                setTableData([...currentData]);
                hot.render();

                applyQuotaIfAny(result.quota);
            } else {
                message.error(result.message || '조회 실패');
            }
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            message.error(apiErrToMessage(data, status));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setTableData(Array.from({ length: maxRows }, () => Array(10).fill('')));
    };

    const handleDelete = async id => {
        Modal.confirm({
            title: '정말 삭제하시겠습니까?',
            content: '삭제된 기록은 되돌릴 수 없습니다.',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await AuthService.authAxios.post('/log/delete', { id });
                    setHistoryData(prev => prev.filter(item => item.id !== id));
                    message.success('삭제 완료');
                } catch (err) {
                    console.error('삭제 실패:', err);
                    message.error('삭제 중 오류가 발생했습니다.');
                }
            },
        });
    };

    const fetchHistory = async (nextOffset = 0, append = false) => {
        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: { gubun: 'findProductByMid', offset: nextOffset },
            });

            if (res.data.success) {
                const newData = res.data.data;
                if (!append) setHasMore(true);
                if (newData.length < PAGE_SIZE) setHasMore(false);

                if (append) {
                    setHistoryData(prev => [...prev, ...newData]);
                    setOffset(prev => prev + PAGE_SIZE);
                } else {
                    setHistoryData(newData);
                    setOffset(PAGE_SIZE);
                }
            }
        } catch (err) {
            console.error('조회 기록 불러오기 실패:', err);
        } finally {
            setHistoryLoaded(true);
        }
    };

    const handleExportExcel = () => {
        try {
            const headers = [
                '키워드',
                '상품번호 or MID',
                '상품주소',
                '원부 MID',
                '상품명',
                '스토어명',
                '썸네일',
                '가격',
                '태그',
                '상태',
            ];

            const filteredData = tableData.filter(row => row.some(cell => cell && cell !== ''));

            if (filteredData.length === 0) {
                message.warning('저장할 결과가 없습니다.');
                return;
            }

            const exportData = filteredData.map(row => ({
                키워드: row[0] || '',
                '상품번호 or MID': row[1] || '',
                상품주소: row[2] || '',
                '원부 MID': row[3] || '',
                상품명: row[4] || '',
                스토어명: row[5] || '',
                썸네일: row[6] || '',
                가격: row[7] || '',
                태그: row[8] || '',
                상태: plainStatusForExport(row[9]),
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData, { header: headers });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, '상품정보조회결과');

            const now = new Date();
            const timestamp = now.toISOString().slice(0, 19).replace(/[-T:]/g, '');
            const filename = `상품정보조회결과_${timestamp}.xlsx`;

            XLSX.writeFile(workbook, filename);
        } catch (error) {
            console.error('❌ 엑셀 다운로드 오류:', error);
            message.error('엑셀 다운로드 중 오류가 발생했습니다.');
        }
    };

    useEffect(() => {
        localStorage.setItem('findProductByMid_showGuide', showGuide);
    }, [showGuide]);

    const applyQuotaIfAny = obj => {
        if (!obj || typeof obj !== 'object') return;
        const has = k => Object.prototype.hasOwnProperty.call(obj, k);
        if (has('used') || has('limit') || has('bonus') || has('remaining') || has('day')) {
            dispatchQuotaUpdated('findProductByMid');
        }
    };

    return (
        <ProtectedMenuPage menuKey="findProductByMid">
            <div className="find-product-by-mid-page">
                <div className="page-header-container no-header-border">
                    <Card className="control-card" bodyStyle={{ padding: '8px 16px' }}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Text type="secondary">사용 가능 행 수: </Text>
                                <Text strong>{maxRows}</Text>
                            </Col>
                            <Col>
                                <Space size="small">
                                    <Button
                                        type="primary"
                                        className="search-btn-premium"
                                        onClick={handleMidSearch}
                                        loading={loading}
                                        icon={<SearchOutlined />}
                                    >
                                        조회하기
                                    </Button>
                                    <Button
                                        className="page-action-btn"
                                        icon={<ReloadOutlined />}
                                        onClick={handleReset}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        className="history-btn"
                                        icon={<HistoryOutlined />}
                                        onClick={() => {
                                            setShowHistoryModal(true);
                                            fetchHistory();
                                        }}
                                    >
                                        기록
                                    </Button>
                                    <Button
                                        className="excel-button"
                                        icon={<DownloadOutlined />}
                                        onClick={handleExportExcel}
                                        title="현재 조인된 데이터를 엑셀로 저장"
                                    />
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </div>

                <div className="page-content-inner">
                    <div className="find-product-by-mid-table">
                        <HotTable
                            ref={hotRef}
                            data={tableData}
                            colHeaders={[
                                '키워드 <span class="text-danger">*</span>',
                                '상품번호 or MID <span class="text-danger">*</span>',
                                '상품주소',
                                '원부 MID',
                                '상품명',
                                '스토어명',
                                '썸네일',
                                '가격',
                                '태그',
                                '상태',
                            ]}
                            contextMenu={{
                                items: {
                                    undo: {
                                        name: '되돌리기',
                                        hidden: function () {
                                            return !this.getPlugin('undoRedo')?.isUndoAvailable();
                                        },
                                    },
                                    redo: {
                                        name: '다시 실행하기',
                                        hidden: function () {
                                            return !this.getPlugin('undoRedo')?.isRedoAvailable();
                                        },
                                    },
                                    copy: { name: '복사' },
                                    cut: { name: '잘라내기' },
                                },
                            }}
                            rowHeaders={true}
                            manualColumnResize={true}
                            stretchH="all"
                            height="100%"
                            width="100%"
                            colWidths={colWidths}
                            afterColumnResize={(newSize, column) => {
                                setColWidths(prev => {
                                    const next = [...prev];
                                    next[column] = newSize;
                                    saveColWidths(next);
                                    return next;
                                });
                            }}
                            cells={(row, col) => {
                                const align =
                                    col === 0 || col === 2 || col === 3 || col === 5 || col === 9
                                        ? 'htCenter'
                                        : col === 7
                                          ? 'htRight'
                                          : 'htLeft';
                                const base =
                                    col <= 1
                                        ? { readOnly: false, className: 'required-column' }
                                        : {
                                              readOnly: true,
                                              renderer:
                                                  col === 9 ? hotStatusPillRenderer : undefined,
                                          };
                                return {
                                    ...base,
                                    className: [base.className, align].filter(Boolean).join(' '),
                                };
                            }}
                            beforeChange={(changes, source) => {
                                if (!changes) return;
                                const isPaste = source === 'CopyPaste.paste';
                                if (isPaste) {
                                    const maxRowTouched = Math.max(...changes.map(([r]) => r));
                                    if (maxRowTouched + 1 > maxRows) {
                                        message.warning(
                                            `최대 ${maxRows}행까지만 입력할 수 있습니다.`
                                        );
                                        return false;
                                    }
                                }
                            }}
                            licenseKey="non-commercial-and-evaluation"
                        />
                    </div>
                </div>

                <Modal
                    title="상품정보 조회 기록"
                    open={showHistoryModal}
                    onCancel={() => setShowHistoryModal(false)}
                    centered
                    mask={{ blur: true }}
                    footer={[
                        <Button key="close" onClick={() => setShowHistoryModal(false)}>
                            닫기
                        </Button>,
                    ]}
                    width={800}
                    className="history-modal"
                    bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                    {!historyLoaded ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                        </div>
                    ) : historyData.length === 0 ? (
                        <Empty description="조회 기록이 없습니다." />
                    ) : (
                        <>
                            <List
                                dataSource={historyData}
                                renderItem={item => {
                                    const parts = {};
                                    (item.input || '').split('|').forEach(pair => {
                                        const [key, value] = pair.split('=');
                                        if (key) parts[key] = value;
                                    });

                                    return (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="delete"
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    삭제
                                                </Button>,
                                            ]}
                                        >
                                            <List.Item.Meta
                                                title={
                                                    <Space
                                                        direction="vertical"
                                                        style={{ width: '100%' }}
                                                    >
                                                        <Space split={<Divider type="vertical" />}>
                                                            <Text strong>
                                                                스토어: {parts.channel_name || '-'}
                                                            </Text>
                                                            <Text>
                                                                키워드: {parts.keyword || '-'}
                                                            </Text>
                                                        </Space>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '13px' }}
                                                        >
                                                            상품명: {parts.product_name || '-'}
                                                        </Text>
                                                    </Space>
                                                }
                                                description={
                                                    <div style={{ marginTop: '8px' }}>
                                                        <div
                                                            style={{
                                                                wordBreak: 'break-all',
                                                                marginBottom: '4px',
                                                            }}
                                                        >
                                                            <Text
                                                                type="secondary"
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                URL: {parts.url || '-'}
                                                            </Text>
                                                        </div>
                                                        <Space split={<Divider type="vertical" />}>
                                                            <Text
                                                                type="secondary"
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                MID: {parts.syncNvMid || '-'}
                                                            </Text>
                                                            <Text
                                                                type="secondary"
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                원부: {parts.parentId || '-'}
                                                            </Text>
                                                            <Text
                                                                type="secondary"
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                {item.updated_at ||
                                                                    item.searched_at}
                                                            </Text>
                                                        </Space>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    );
                                }}
                            />
                            {hasMore && historyData.length > 0 && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        marginTop: '16px',
                                    }}
                                >
                                    <Button
                                        onClick={() => fetchHistory(offset, true)}
                                        loading={loading}
                                    >
                                        더보기
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Modal>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { FindProductByMid };
FindProductByMid.propTypes = {
    title: PropTypes.string,
};
